import { useState, useEffect, useLayoutEffect, lazy, Suspense, useMemo, useRef } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router";
import { isEmpty } from "lodash";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";

import {
  changeSidebarType,
  changeSidebarTheme,
  changeLayoutWidth,
  changeTopbarTheme,
} from "../../../actions/layout";
import ErrorBoundary from "../../ErrorBoundary";
import { capitalizedPathName } from "../../../helpers/string";
import Footer from "./Footer";
import useTheme from "../../../helpers/useTheme";
import PreLoader from "../../common/PreLoader";
import {
  isHoaxMerchDomain,
  isLarevivalDomain,
  currentDomain,
  isDevDomain,
  isPrintifyDomain,
  isRakilineDomain,
  isTeeprintcentreDomain,
  isTheSublimationStationDomian,
  isUrbanprintshopDomian,
  isDallasPrintHouseDomian,
  shouldHidePaymentMethodRequirement,
  isOmniCommerceContext,
} from "../../../utils";
import { shouldSkipSaaSPricingUi } from "../../../helpers/saasPricingGate";
import "./styles.scss";
import "./AccessStrip.scss";
import AccessStrip from "./AccessStrip";
import DunningBanner from "../../common/DunningBanner";
import PendingUpgradeBanner from "../../common/PendingUpgradeBanner";
import SellerHoldBanner from "../../common/SellerHoldBanner";
import SellerPriceChangeBanner from "../../common/SellerPriceChangeBanner";
import SellerMarketingPrompt from "./SellerMarketingPrompt";
import SetPasswordPrompt from "./SetPasswordPrompt";
import SellerOnboardingGate from "./SellerOnboardingGate";
import AdminHoldBanner from "../../common/AdminHoldBanner";
import EnterpriseAnnouncementBanner from "../../common/EnterpriseAnnouncementBanner";
import PrintAppInstallDialog from "../../common/PrintAppInstallDialog";
import StaffNameDialog from "../../common/StaffNameDialog";
import StaffIdleLockGate from "../../common/StaffIdleLockGate";
import LoadChatBot from "./LoadChatBot";
import AskPanel from "./AskPanel";
import { AskProvider } from "../../../helpers/AskContext";
import { useFeatureFlag } from "../../../helpers/featureFlags";
// NOTE: `RightDrawer` (which imports `@shopify/polaris`) was imported here but
// never rendered — a dead import that pulled Polaris into `main.js`. Removed so
// Polaris only ships via the lazy AdminProviders chunk.
import { RIGHT_DRAWER_WIDTH } from "../../../constants/dashboard";
import { logoutUser } from "../../../actions/authentication";
import {
  parsePopupStoreIdFromPath,
  shouldUsePopupStoreSidebar,
  RIVER_LAST_NON_STORE_PATH_KEY,
  isValidPopupStoreExitPath,
} from "../../../helpers/popupStoreNav";
import { shopsSelector } from "../../../selectors/shops";
import {
  isKornitSettingsPath,
  RIVERR_LAST_NON_KORNIT_PATH_KEY,
} from "../../../helpers/kornitNav";
import {
  isSettingsPath,
  isValidSettingsTab,
  RIVERR_LAST_NON_SETTINGS_PATH_KEY,
  RIVERR_LAST_SETTINGS_TAB_KEY,
} from "../../../helpers/settingsNav";
import {
  isBlankSettingsPath,
  RIVERR_LAST_NON_BLANK_SETTINGS_PATH_KEY,
} from "../../../helpers/blankSettingsNav";
// import Avatar from "../../../assets/images/Avatar.png";
// import ItemScannerField from "../../common/ItemScannerField";
import {
  exitImpersonation,
  getMockUser,
  impersonationLabel,
  isImpersonating,
} from "../../../helpers/impersonation";
import { Alert } from "@mui/material";
import moment from "moment";
import { getFirebaseBackend } from "../../../helpers/firebase_helper";
import { requestShops } from "../../../actions/shops";
import { noPaymentMethodsAvailableSelector, isLockedToPricingSelector } from "../../../selectors/credentials";
import { getNotifications } from "../../../actions/notifications";
import { adoptPrinterSession } from "../../../api/user";
import { applyTenantBrand } from "../../../helpers/brandColor";
import { sellerPathAllowed } from "../../../helpers/sellerAccess";
import { staffPathAllowed } from "../../../helpers/staffPaths";
import useColorMode from "../../../helpers/useColorMode";
import {
  resolveAssetUrl,
  resolveThemeWordmarkUrl,
} from "../../../helpers/themeLogo";

const HoaxMerchHeader = lazy(() => import("./HoaxMerch/Header"));
const HoaxMerchSidebar = lazy(() => import("./HoaxMerch/Sidebar"));
const LarevivalHeader = lazy(() => import("./Larevival/Header"));
const LarevivalSidebar = lazy(() => import("./Larevival/Sidebar"));
const RiverrHeader = lazy(() => import("./Riverr/Header"));
const RiverrSidebar = lazy(() => import("./Riverr/Sidebar"));

const fireBaseBackend = getFirebaseBackend();

const VerticalLayout = ({
  children,
  leftSideBarType,
  leftSideBarTheme,
  layoutWidth,
  topbarTheme,
  currentUser,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const mockUser = getMockUser();
  const isMockUser = isImpersonating(mockUser);

  const { enterprise, logo, logoSymbol, logoDark, primaryColor } = useTheme();
  const { mode: colorMode } = useColorMode();

  // Push the tenant `primaryColor` onto the three brand CSS
  // custom properties (`--brand`, `--brand-dark`, `--brand-soft`).
  // The chrome neutrals (`--bg-page`, `--bg-card`, sidebar tokens)
  // are STATIC and live in `assets/scss/_design-tokens.scss`; only
  // the three brand tokens swap per tenant. `applyTenantBrand`
  // validates contrast against `--bg-card` and auto-darkens
  // sub-threshold inputs so a tenant with a too-light brand colour
  // can't make the Create-button label unreadable.
  useEffect(() => {
    applyTenantBrand(primaryColor);
  }, [primaryColor]);

  useLayoutEffect(() => {
    if (isMockUser) {
      document.body.classList.add("riverr-seller-access");
    } else {
      document.body.classList.remove("riverr-seller-access");
    }
    return () => document.body.classList.remove("riverr-seller-access");
  }, [isMockUser]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    if (sellerPathAllowed(currentUser, location.pathname)) return;
    navigate("/dashboard", { replace: true });
  }, [currentUser, location.pathname, navigate]);

  // Custom staff only open what they were given, by URL as well as the nav.
  // "/" sends them to their own home (their station, or the first page they have).
  useEffect(() => {
    if (!currentUser?.uid) return;
    if (staffPathAllowed(currentUser, location.pathname)) return;
    navigate("/", { replace: true });
  }, [currentUser, location.pathname, navigate]);

  const [isMobile, setIsMobile] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [adoptingPrinter, setAdoptingPrinter] = useState(false);
  const [adoptPrinterError, setAdoptPrinterError] = useState("");
  const adoptPrinterStarted = useRef(false);
  const assistEnabled = useFeatureFlag("assist.enabled");
  const manowarRag = useFeatureFlag("assist.manowarRag");
  const nativeAsk =
    assistEnabled && !manowarRag && !!currentUser.isEnterpriseAdmin;

  const currentActiveStep = useSelector(
    (state) => state?.dashboard?.currentActiveStep
  );

  const currentEnterprise = useSelector(
    (state) => state.currentUser.enterprise
  );
  const paymentMethods = useSelector(
    (state) => state.credentials.paymentMethods
  );
  const paymentMethodsLoading = useSelector(
    (state) => state?.credentials?.loading
  );
  const planId = useSelector((state) => state?.pricings?.planId);
  const subscription = useSelector((state) => state.currentUser.subscription);

  const { daysLeft, isTrialAlertActive } = useMemo(() => {
    // OmniCommerce is on a real SaaS trial (they will be charged). The
    // countdown strip is still a Riverr app-billing surface, so keep it
    // off this tenant even when the Shopify holds are off.
    if (isOmniCommerceContext(currentEnterprise)) {
      return { daysLeft: 0, isTrialAlertActive: false };
    }
    // Prefer Stripe/ops subscription.trial_end; fall back to opsTrial.endsAt
    // for manually granted enterprise trials.
    let trialEndUnix = subscription?.trial_end || null;
    if (!trialEndUnix) {
      const endsAt = currentEnterprise?.opsTrial?.endsAt;
      const seconds = endsAt?._seconds ?? endsAt?.seconds;
      if (seconds != null) trialEndUnix = Number(seconds);
      else if (typeof endsAt === "number") {
        trialEndUnix = endsAt > 1e12 ? Math.floor(endsAt / 1000) : endsAt;
      }
    }
    if (!trialEndUnix) {
      return { daysLeft: 0, isTrialAlertActive: false };
    }
    // ceil so a fresh 14-day trial shows "14 days" rather than "13".
    const daysLeft = Math.max(
      0,
      Math.ceil(moment.unix(trialEndUnix).diff(moment(), "days", true))
    );
    return { daysLeft, isTrialAlertActive: daysLeft > 0 };
  }, [
    subscription?.trial_end,
    currentEnterprise?.opsTrial?.endsAt,
    currentEnterprise,
  ]);

  const noPaymentMethodsAvailable = useSelector(
    noPaymentMethodsAvailableSelector
  );

  const accessStoreLabel = useMemo(() => {
    if (!isMockUser) return "";
    return impersonationLabel(mockUser, enterprise);
  }, [isMockUser, enterprise, mockUser]);

  const isLockedToPricing = useSelector(isLockedToPricingSelector);
  const shops = useSelector(shopsSelector) || [];

  useEffect(() => {
    const fullPath = `${location.pathname}${location.search || ""}`;
    const popupShopId = parsePopupStoreIdFromPath(location.pathname);
    const popupShop = shops.find((s) => String(s?.id) === String(popupShopId));
    const isPopupStoreAdmin = shouldUsePopupStoreSidebar(
      location.pathname,
      popupShop
    );
    const isKornit = isKornitSettingsPath(location.pathname);
    const isSettings = isSettingsPath(location.pathname);
    const isBlankSettings = isBlankSettingsPath(location.pathname);

    if (!isPopupStoreAdmin && isValidPopupStoreExitPath(location.pathname)) {
      try {
        sessionStorage.setItem(RIVER_LAST_NON_STORE_PATH_KEY, fullPath);
      } catch {
        /* ignore quota / private mode */
      }
    }
    if (!isKornit) {
      try {
        sessionStorage.setItem(RIVERR_LAST_NON_KORNIT_PATH_KEY, fullPath);
      } catch {
        /* ignore */
      }
    }
    if (!isBlankSettings) {
      try {
        sessionStorage.setItem(
          RIVERR_LAST_NON_BLANK_SETTINGS_PATH_KEY,
          fullPath
        );
      } catch {
        /* ignore */
      }
    }
    if (!isSettings && !isPopupStoreAdmin && !isKornit && !isBlankSettings) {
      try {
        sessionStorage.setItem(RIVERR_LAST_NON_SETTINGS_PATH_KEY, fullPath);
      } catch {
        /* ignore */
      }
    } else if (isSettings) {
      // Remember the active settings leaf so coming back to /settings later
      // restores it. Leaf ids may contain a slash (e.g. `catalog/blank-products`),
      // so capture everything after `/settings/` up to (but not including) any
      // deeper sub-route the page itself uses internally.
      const m = location.pathname.match(/^\/settings\/(.+?)(?:\/|$)/);
      // Walk back from the most-specific segment to the least until we find a
      // valid leaf id. Handles both single-segment (`profile`) and two-segment
      // (`catalog/blank-products`) leaves without overshooting into page tabs.
      const after = location.pathname.replace(/^\/settings\//, "");
      const segs = after.split("/").filter(Boolean);
      let leafId = null;
      while (segs.length) {
        const candidate = segs.join("/");
        if (isValidSettingsTab(candidate)) {
          leafId = candidate;
          break;
        }
        segs.pop();
      }
      if (leafId || (m && m[1])) {
        try {
          sessionStorage.setItem(
            RIVERR_LAST_SETTINGS_TAB_KEY,
            leafId || m[1]
          );
        } catch {
          /* ignore */
        }
      }
    }
  }, [location.pathname, location.search, shops]);

  // redirect admins to pricing page if locked (accounts.riverr.app signup without plan)
  useEffect(() => {
    if (
      isLockedToPricing &&
      location.pathname !== "/pricing" &&
      !isSettingsPath(location.pathname) &&
      !isKornitSettingsPath(location.pathname)
    ) {
      navigate("/pricing");
    }
  }, [isLockedToPricing, location.pathname, navigate]);

  // redirect admins to profile page if no credit card details added
  useEffect(() => {
    if (
      !isEmpty(currentUser) &&
      currentUser.isEnterpriseAdmin &&
      !currentUser.isProUser &&
      !paymentMethodsLoading &&
      !isDevDomain &&
      !isPrintifyDomain &&
      !isRakilineDomain &&
      !isTeeprintcentreDomain &&
      !isTheSublimationStationDomian &&
      !shouldHidePaymentMethodRequirement(currentEnterprise, currentUser) &&
      !shouldSkipSaaSPricingUi(currentUser, currentEnterprise) &&
      !isUrbanprintshopDomian &&
      !isDallasPrintHouseDomian
    ) {
      if (
        !["/pricing", "/profile"].includes(location.pathname) &&
        !isSettingsPath(location.pathname) &&
        !isKornitSettingsPath(location.pathname) &&
        (noPaymentMethodsAvailable || (!isEmpty(paymentMethods) && !planId))
      ) {
        navigate("/pricing");
      }
    }
  }, [
    currentUser,
    currentEnterprise,
    paymentMethodsLoading,
    location.pathname,
    isDevDomain,
    noPaymentMethodsAvailable,
    paymentMethods,
    planId,
    navigate,
  ]);

  // check if the logged in user is anonymous and redirect to store creation flow
  useEffect(() => {
    if (isEmpty(currentUser)) {
      const userToCheck = fireBaseBackend.getAuthenticatedUser();
      const isAnonymousUser = userToCheck && userToCheck.isAnonymous;

      if (isAnonymousUser) {
        dispatch(requestShops()).then((res) => {
          const firstShopId = res?.payload?.data?.shops?.[0]?.id;

          if (firstShopId) {
            navigate(`/public-store/${firstShopId}/complete-signup`);
          } else {
            navigate(`/public-store/new`);
          }
        });
      }
    }
  }, [currentUser]);

  useEffect(() => {
    setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
  }, []);

  useEffect(() => {
    if (currentUser.isEnterpriseAdmin) {
      document.body.classList.add("is-enterprise-admin");
    } else {
      document.body.classList.remove("is-enterprise-admin");
    }

    if (isTrialAlertActive) {
      document.body.classList.add("isTrialAlertActive");
    } else {
      document.body.classList.remove("isTrialAlertActive");
    }
  }, [currentUser, isTrialAlertActive]);

  useEffect(() => {
    window.scrollTo(0, 0);
    let currentpage = capitalizedPathName(location.pathname);

    const onDtgStation =
      location.pathname === "/machines" ||
      location.pathname.startsWith("/machines/");
    document.title = onDtgStation
      ? currentpage
      : currentpage + ` | ${enterprise?.name}`;
    if (leftSideBarTheme) {
      dispatch(changeSidebarTheme(leftSideBarTheme));
    }

    if (layoutWidth) {
      dispatch(changeLayoutWidth(layoutWidth));
    }

    if (leftSideBarType) {
      dispatch(changeSidebarType(leftSideBarType));
    }
    if (topbarTheme) {
      dispatch(changeTopbarTheme(topbarTheme));
    }
  }, [
    dispatch,
    layoutWidth,
    leftSideBarTheme,
    leftSideBarType,
    topbarTheme,
    location.pathname,
  ]);

  // Collapse temporarily disabled — keep the expanded rail only.
  const toggleMenuCallback = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    if (leftSideBarType === "condensed") {
      dispatch(changeSidebarType("default", isMobile));
    }
  }, [dispatch, isMobile, leftSideBarType]);

  const DynamicComponents = (function () {
    if (isHoaxMerchDomain) {
      return {
        HeaderComponent: HoaxMerchHeader,
        SidebarComponent: HoaxMerchSidebar,
      };
    }

    if (isLarevivalDomain) {
      return {
        HeaderComponent: LarevivalHeader,
        SidebarComponent: LarevivalSidebar,
      };
    }

    return {
      HeaderComponent: RiverrHeader,
      SidebarComponent: RiverrSidebar,
    };
  })();

  let isUserFromCurrentDomain = true;

  if (!isEmpty(currentEnterprise?.hostnames) && currentDomain) {
    isUserFromCurrentDomain =
      currentEnterprise?.hostnames?.includes(currentDomain);
  }

  useEffect(() => {
    // Other initial calls are likely in AllInitialApiCallsComponent, but adding this here since NotificationListener was here
    if (currentUser?.uid) {
      dispatch(getNotifications());
    }
  }, [currentUser?.uid, dispatch]);

  useEffect(() => {
    if (isUserFromCurrentDomain || isDevDomain) return;
    if (adoptPrinterStarted.current) return;
    adoptPrinterStarted.current = true;
    setAdoptingPrinter(true);
    adoptPrinterSession()
      .then(async (res) => {
        const customToken = res?.data?.customToken || res?.customToken;
        if (!customToken) {
          throw new Error("Could not open this printer.");
        }
        await fireBaseBackend.loginUser(null, null, customToken);
        window.location.replace(
          `${window.location.pathname}${window.location.search}`
        );
      })
      .catch((error) => {
        setAdoptPrinterError(
          error?.response?.data?.message ||
            error?.message ||
            "Could not open this printer."
        );
        setAdoptingPrinter(false);
      });
  }, [isUserFromCurrentDomain]);

  const handleMockUserReset = () => {
    exitImpersonation();
  };

  if (!isUserFromCurrentDomain && !isDevDomain) {
    return (
      <>
        {isMockUser && (
          <AccessStrip
            storeLabel={accessStoreLabel}
            onExit={handleMockUserReset}
            underTrial={false}
          />
        )}
        <div className="p-5">
          <p className="h2">
            {adoptingPrinter || !adoptPrinterError
              ? "Opening this printer…"
              : adoptPrinterError}
          </p>
          {!adoptingPrinter && adoptPrinterError ? (
            <div className="mt-3">
              {isMockUser ? (
                <>
                  Click here to{" "}
                  <b
                    role="button"
                    onClick={handleMockUserReset}
                    className="text-primary"
                  >
                    Back to admin
                  </b>{" "}
                  or{" "}
                  <b
                    role="button"
                    onClick={() => dispatch(logoutUser())}
                    className="text-primary"
                  >
                    Logout
                  </b>{" "}
                  and continue on this site.
                </>
              ) : (
                <>
                  Click here to{" "}
                  <b
                    role="button"
                    onClick={() => dispatch(logoutUser())}
                    className="text-primary"
                  >
                    Logout
                  </b>{" "}
                  and continue on this site.
                </>
              )}
            </div>
          ) : isMockUser ? (
            <div className="mt-3">
              Click here to{" "}
              <b
                role="button"
                onClick={handleMockUserReset}
                className="text-primary"
              >
                Back to admin
              </b>
            </div>
          ) : null}
        </div>
      </>
    );
  }

  const showFooter = false; // !location.pathname.includes("nester");
  // Theme assets may be `{ url }` objects or legacy bare URL strings.
  // Prefer the wordmark for the expanded rail; fall back either way so a
  // tenant that only uploaded one asset still shows a mark in the top-left.
  // Dark mode uses `logoDark` when uploaded; otherwise the main logo.
  const LOGO_IMG =
    resolveThemeWordmarkUrl(
      { logo, logoDark, logoSymbol, enterprise },
      { mode: colorMode }
    ) || resolveAssetUrl(currentEnterprise?.logo);
  const COMPRESSED_LOGO_IMG =
    resolveAssetUrl(logoSymbol) ||
    LOGO_IMG;
  // "https://storage.googleapis.com/riverr-enterprise-user.appspot.com/users%2FzNAlxdaZG6hKf6vexv6ljqHyP8i1%2Fmanual-products%2F1666429944058PD061_1_1024x1024.webp";

  // Trial strip is `position: fixed` at the very top (legacy). The
  // admin seller-access strip is ALSO `position: fixed` at top: 0
  // (see `VerticalLayout/styles.scss::.riverr-access-strip`); the
  // topbar + sidebar offset 32px south via SCSS so they sit below
  // it. Both top fixtures are mutually exclusive in practice (admin
  // impersonating a seller rarely also has an active trial), but
  // the math below stacks them safely if both ever activate.
  const TRIAL_ALERT_HEIGHT = 57;
  const trialOffset = isTrialAlertActive ? TRIAL_ALERT_HEIGHT : 0;
  // Access-strip clearance (and trial+access stacked clearance) is owned
  // by SCSS on `body.riverr-seller-access`. Passing those heights again
  // as marginTop stacked a second offset and left a grey gap between the
  // Accessing strip and the topbar. Trial-only (no mock strip) still
  // uses JS marginTop.
  const legacyChromeTopInset = isMockUser ? 0 : trialOffset;

  const askShopName =
    enterprise?.name || currentEnterprise?.name || currentEnterprise?.companyName || "";
  const askPlatformAdmin = /@(riverr\.app|getriverr\.com)$/i.test(
    currentUser?.email || ""
  );

  return (
    <AskProvider
      enabled={!!nativeAsk}
      shopName={askShopName}
      isPlatformAdmin={askPlatformAdmin}
    >
    <Suspense fallback={<PreLoader />}>
      {manowarRag ? <LoadChatBot {...{ currentUser }} /> : null}
      {nativeAsk ? <AskPanel /> : null}

      {isTrialAlertActive && (
        <Alert
          severity="info"
          variant="filled"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            // Above #page-topbar (z-index 999) so the strip stays visible.
            zIndex: 1100,
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
          }}
          icon={<HourglassBottomIcon />}
        >
          <span style={{ fontSize: 20 }}>
            You have <b>{daysLeft} day(s)</b> left in your free trial.
          </span>
        </Alert>
      )}

      {isMockUser && (
        <AccessStrip
          storeLabel={accessStoreLabel}
          onExit={handleMockUserReset}
          underTrial={isTrialAlertActive}
        />
      )}

      <div id="layout-wrapper">
        <DynamicComponents.HeaderComponent
          toggleMenu={toggleMenuCallback}
          currentUser={currentUser}
          type={leftSideBarType}
          marginRight={currentActiveStep ? RIGHT_DRAWER_WIDTH : "initial"}
          marginTop={legacyChromeTopInset}
        />
        <DynamicComponents.SidebarComponent
          toggleSidebar={toggleMenuCallback}
          theme={leftSideBarTheme}
          type={leftSideBarType}
          isMobile={isMobile}
          logo={LOGO_IMG}
          compressedLogo={COMPRESSED_LOGO_IMG}
          marginTop={legacyChromeTopInset}
        />

        <div
          className="main-content"
          style={{
            minHeight: "calc(100vh - 60px)",
            marginRight: "initial",
            marginTop: legacyChromeTopInset
              ? `${legacyChromeTopInset}px`
              : "initial",
          }}
        >
          <SellerMarketingPrompt />
          <SetPasswordPrompt />
          <SellerOnboardingGate />
          <EnterpriseAnnouncementBanner />
          <SellerPriceChangeBanner />
          <DunningBanner />
          <PendingUpgradeBanner />
          <AdminHoldBanner />
          <SellerHoldBanner />
          <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
          <PrintAppInstallDialog />
          <StaffNameDialog />
          <StaffIdleLockGate />
        </div>
        {showFooter && <Footer />}
        {/* {currentUser?.isEnterpriseAdmin && <ItemScannerField />} */}
      </div>
    </Suspense>
    </AskProvider>
  );
};

const mapStatetoProps = (state) => {
  return {
    ...state.layout,
    currentUser: state.currentUser?.user || {},
  };
};

export default connect(mapStatetoProps)(VerticalLayout);
